/**
 * Rainbow AI Radar - Type Definitions
 *
 * Type definitions for Rainbow AI precipitation radar integration.
 * These interfaces define the data structures used for fetching metadata
 * from the backend API and rendering radar tiles via CloudFront CDN.
 *
 * @see specs/001-rainbow-ai-migration/data-model.md
 */

/**
 * Response from backend `/api/rainbow/metadata` endpoint.
 *
 * Contains current snapshot timestamp, historical snapshots, forecast times,
 * and CloudFront base URL for constructing tile URLs.
 */
export interface RainbowMetadataResponse {
  /**
   * Current snapshot timestamp in Unix seconds (UTC).
   * Aligned to 10-minute intervals (divisible by 600).
   * @example 1701360000
   */
  snapshot: number;

  /**
   * Array of 10 historical snapshot timestamps.
   * Ordered chronologically (oldest first).
   * Each timestamp is 10 minutes (600 seconds) apart.
   * All timestamps within retention window.
   * @example [1701354000, 1701354600, ..., 1701359400]
   */
  past_snapshots: number[];

  /**
   * Array of 16 forecast time offsets in seconds.
   * forecast_time=0 represents current snapshot.
   * forecast_time=9000 represents 2.5 hours in the future.
   * @example [0, 600, 1200, 1800, 2400, 3000, 3600, 4200, 4800, 5400, 6000, 6600, 7200, 7800, 8400, 9000]
   */
  forecast_times: number[];

  /**
   * Timestamp when this response was generated (Unix seconds, UTC).
   * Used for debugging and cache validation.
   * @example 1701360005
   */
  generated: number;

  /**
   * CloudFront distribution base URL for tile requests.
   * Frontend appends: /{snapshot}/{forecast_time}/{z}/{x}/{y}?color=0&coverage=1
   * @example "https://d1a2b3c4d5e6f7.cloudfront.net/tiles/v1/precip"
   */
  cloudfront_base_url: string;
}

/**
 * Represents a single frame in the 26-frame precipitation timeline.
 *
 * Timeline structure:
 * - Frames 0-9: Past observations (type='past')
 * - Frame 10: Current snapshot (type='current')
 * - Frames 11-25: Future forecasts (type='future')
 */
export interface PrecipitationFrame {
  /**
   * Frame index in timeline (0-25).
   * @example 10
   */
  index: number;

  /**
   * Frame type indicating temporal position.
   * - 'past': Historical observation
   * - 'current': Current snapshot (frame 10)
   * - 'future': Forecast prediction
   */
  type: "past" | "current" | "future";

  /**
   * Snapshot timestamp used for this frame (Unix seconds, UTC).
   * For past/current frames: the observation timestamp.
   * For future frames: the base snapshot timestamp (forecast origin).
   * @example 1701360000
   */
  snapshot: number;

  /**
   * Forecast time offset in seconds.
   * - Past/current frames: 0
   * - Future frames: 600, 1200, ..., 9000
   * @example 0
   */
  forecast_time: number;

  /**
   * Effective timestamp for this frame (Unix seconds, UTC).
   * For past/current: equals snapshot.
   * For future: equals snapshot + forecast_time.
   * Used for timeline display and sorting.
   * @example 1701360000
   */
  timestamp: number;

  /**
   * Tile URL pattern for this frame.
   * Contains placeholders {z}/{x}/{y} for Leaflet tile coordinates.
   * Includes query params: color=0 (colormap ID) and coverage=1 (show coverage map).
   * @example "https://d1a2b3c4d5e6f7.cloudfront.net/tiles/v1/precip/1701360000/0/{z}/{x}/{y}?color=0&coverage=1"
   */
  tileUrlPattern: string;
}

/**
 * Validation rules for RainbowMetadataResponse.
 * Used by RadarService to validate API responses.
 */
export const RAINBOW_METADATA_VALIDATION = {
  /**
   * Snapshot must be aligned to 10-minute intervals (divisible by 600).
   */
  SNAPSHOT_INTERVAL: 600,

  /**
   * Past snapshots must contain exactly 10 timestamps.
   */
  PAST_SNAPSHOTS_COUNT: 10,

  /**
   * Forecast times must contain exactly 16 offsets.
   */
  FORECAST_TIMES_COUNT: 16,

  /**
   * Maximum retention window: 1 hour 40 minutes (6000 seconds).
   * 10 past snapshots × 10 minutes = 100 minutes
   */
  MAX_RETENTION_WINDOW: 6000,

  /**
   * CloudFront URL must start with https://.
   */
  CLOUDFRONT_URL_PATTERN: /^https:\/\/.+/,
} as const;

/**
 * Timeline constants for 26-frame radar animation.
 */
export const TIMELINE_CONSTANTS = {
  /**
   * Total number of frames in timeline.
   * 10 past + 1 current + 15 future = 26 frames
   */
  TOTAL_FRAMES: 26,

  /**
   * Number of past observation frames.
   */
  PAST_FRAMES: 10,

  /**
   * Index of current snapshot frame.
   */
  CURRENT_FRAME_INDEX: 10,

  /**
   * Number of future forecast frames.
   */
  FUTURE_FRAMES: 15,

  /**
   * Time interval between frames (10 minutes in seconds).
   */
  FRAME_INTERVAL: 600,

  /**
   * Total timeline duration (4 hours 10 minutes in seconds).
   * 1h40m past (10 frames) + 2h30m future (15 frames) = 4h10m total
   */
  TIMELINE_DURATION: 15000,
} as const;

/**
 * Rainbow AI colormap ID for precipitation visualization.
 * ID 0 provides rain (blue) and snow (white/purple) differentiation.
 */
export const RAINBOW_COLORMAP_ID = 2;

/**
 * Query parameters for Rainbow AI tile requests.
 */
export interface RainbowTileParams {
  /**
   * Colormap ID (0 = default rain/snow differentiation).
   */
  color: number;

  /**
   * Coverage map toggle (1 = show data boundaries, 0 = hide).
   */
  coverage: number;
}

/**
 * Default tile query parameters.
 */
export const DEFAULT_TILE_PARAMS: RainbowTileParams = {
  color: RAINBOW_COLORMAP_ID,
  coverage: 1,
} as const;
